import type { BulkImportConfig } from '@/features/institute/admissions/components/BulkImportPanel';
import type { ImportResult } from '@/types/institute';
import type { StaffImportResult } from '@/types/staff';
import { staffApi } from './api/staff.api';

/**
 * The staff room's spreadsheet import, told to the shared import steps.
 *
 * The staff dialog once used the student import as it was — so its template
 * was the student sheet, and an upload would have admitted the staff list as
 * children. This is what points the same steps at the staff endpoints.
 */

/**
 * The server's staff result, in the shape the shared steps read.
 *
 * The steps were written for admissions and call a row that went in
 * `ADMITTED`; the label the office sees comes from `done` below, so "Added" is
 * what is shown. The employee ID takes the slot the admission number uses.
 */
function toImportResult(result: StaffImportResult): ImportResult {
  return {
    totalRows: result.totalRows,
    admitted: result.added,
    skipped: result.skipped,
    rejected: result.rejected,
    rows: result.rows.map((row) => ({
      row: row.row,
      name: row.name,
      status: row.status === 'ADDED' ? 'ADMITTED' : row.status,
      reason: row.reason,
      admissionNumber: row.employeeCode,
    })),
  };
}

export const STAFF_IMPORT: BulkImportConfig = {
  templatePath: staffApi.templatePath(),
  templateFileName: 'staff-import-template.xlsx',
  upload: async (sheet) => toImportResult(await staffApi.importSheet(sheet)),
  noun: { one: 'staff member', many: 'staff members' },
  done: 'added',
  alreadyThere: 'already on the staff list',
  fillInHint: (
    <>
      Delete the example row. Write dates as <strong>YYYY-MM-DD</strong>. The Role must match one
      set up at your institute — the Notes sheet lists them. Each person is emailed a link to set
      their own password.
    </>
  ),
  uploadHint:
    'Excel (.xlsx) or CSV. An email address that already has an account is skipped, so uploading a corrected sheet again is safe.',
};
